import { ReactNode, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { isAdmin } from "@/lib/adminAuth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Building2,
  TrendingUp,
  Settings,
  LogOut,
  MessageCircle,
  Shield,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useJourneyStatus } from "@/hooks/useJourneyStatus";
import wealthyStewardLogo from "@/assets/wealthy-steward-logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MobileHeader } from "./MobileHeader";
import { MobileBottomNav } from "./MobileBottomNav";
import Footer from "./Footer";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { InstallAppMenuItem } from "@/components/pwa/InstallAppMenuItem";

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout = ({ children }: MainLayoutProps) => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const { journeyCompleted } = useJourneyStatus(user?.id);

  useEffect(() => {
    if (user) {
      isAdmin(user.id).then(setIsUserAdmin);
    }
  }, [user]);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // Three persistent entries. My Journey only appears until it is completed.
  const navigation = [
    ...(!journeyCompleted
      ? [{ name: "My Journey", path: "/journey", icon: TrendingUp }]
      : []),
    { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
    { name: "Governance", path: "/governance", icon: Building2 },
    { name: "Support", path: "/support", icon: MessageCircle },
  ];
  
  const getInitials = () => {
    if (!user?.email) return "U";
    return user.email.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen gold-gradient-bg flex flex-col">
      {/* Viewport-anchored gold-to-cream wash. Fixed so every screenful
          matches regardless of scroll or page length. Sits behind all
          content; portals (dialogs, dropdowns, toasts) render above. */}
      <div
        aria-hidden="true"
        className="viewport-gold-backdrop fixed inset-0 -z-10 pointer-events-none"
      />
      {/* Mobile Header - visible only on mobile */}
      <div className="md:hidden">
        <MobileHeader journeyCompleted={journeyCompleted} isAdmin={isUserAdmin} />
      </div>

      {/* Desktop Navigation - hidden on mobile */}
      <header className="hidden md:block sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="container flex h-16 items-center px-4">
          {/* Logo */}
          <Link to="/journey" className="flex items-center mr-8 flex-shrink-0">
            <img src={wealthyStewardLogo} alt="Wealthy Steward" className="h-12 w-auto" />
          </Link>

          {/* Main Navigation */}
          <nav className="flex items-center justify-center gap-1 flex-1">
            <TooltipProvider>
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant="ghost"
                      className={cn(
                        "gap-2 text-charcoal hover:text-charcoal",
                        isActive(item.path) && "bg-gold/30 text-charcoal font-semibold border border-gold/50 hover:bg-gold/40",
                      )}
                      size="sm"
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden md:inline">{item.name}</span>
                    </Button>
                  </Link>
                );
              })}
            </TooltipProvider>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
              <div className="w-8 h-8 rounded-full bg-gold text-charcoal flex items-center justify-center text-sm font-medium">
                    {getInitials()}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">My Account</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                {isUserAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin/dashboard" className="cursor-pointer">
                      <Shield className="mr-2 h-4 w-4" />
                      Admin Portal
                    </Link>
                  </DropdownMenuItem>
                )}
                <InstallAppMenuItem />
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="cursor-pointer text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content - with padding for mobile bottom nav */}
      <main className="w-full px-4 pt-4 md:pt-5 pb-24 md:pb-8 flex-1">
        {children}
      </main>

      {/* Footer - hidden on mobile, pushed to bottom */}
      <div className="hidden md:block mt-auto">
        <Footer />
      </div>

      {/* Mobile Bottom Nav - visible only on mobile */}
      <div className="md:hidden">
        <MobileBottomNav journeyCompleted={journeyCompleted} />
      </div>
    </div>
  );
};

export default MainLayout;